import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";
import api, { formatApiError } from "../lib/api";

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 bg-[#FDFBF7]"
      data-testid="contact-section"
    >
      <div className="container-narrow grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <span className="eyebrow" data-testid="contact-eyebrow">{t.contact.eyebrow}</span>
          <span className="gold-line mt-4 mb-6" />
          <h2 className="font-serif text-4xl md:text-5xl text-[#0A192F] leading-[1.1] font-medium" data-testid="contact-title">
            {t.contact.title}
          </h2>
          <p className="text-[#334155] text-base md:text-lg leading-relaxed mt-5" data-testid="contact-subtitle">
            {t.contact.subtitle}
          </p>

          <div className="mt-10 space-y-6">
            <ContactInfo Icon={Mail} label={t.contact.info.emailLabel} value={t.contact.info.emailValue} />
            <ContactInfo Icon={Phone} label={t.contact.info.phoneLabel} value={t.contact.info.phoneValue} />
            <ContactInfo Icon={MapPin} label={t.contact.info.addressLabel} value={t.contact.info.addressValue} />
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          onSubmit={onSubmit}
          className="lg:col-span-7 bg-white rounded-2xl p-6 md:p-10 shadow-[0_20px_60px_-30px_rgba(0,43,73,0.2)] border border-black/5"
          data-testid="contact-form"
        >
          {done ? (
            <div className="text-center py-12" data-testid="contact-success">
              <CheckCircle2 size={56} className="text-emerald-500 mx-auto mb-4" />
              <h3 className="font-serif text-2xl text-[#002B49] mb-2 font-semibold">
                {t.contact.success}
              </h3>
              <button
                type="button"
                onClick={() => setDone(false)}
                className="btn-outline-dark mt-6 py-2.5 px-5 text-sm"
                data-testid="contact-reset"
              >
                {t.contact.send}
              </button>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t.contact.name} required>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    className="form-input"
                    data-testid="contact-name-input"
                  />
                </Field>
                <Field label={t.contact.email} required>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className="form-input"
                    data-testid="contact-email-input"
                  />
                </Field>
              </div>
              <Field label={t.contact.subject} required className="mt-4">
                <input
                  type="text"
                  required
                  value={form.subject}
                  onChange={update("subject")}
                  className="form-input"
                  data-testid="contact-subject-input"
                />
              </Field>
              <Field label={t.contact.message} required className="mt-4">
                <textarea
                  required
                  rows="5"
                  value={form.message}
                  onChange={update("message")}
                  className="form-input resize-none"
                  data-testid="contact-message-input"
                />
              </Field>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary mt-6 w-full sm:w-auto justify-center disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {submitting ? "..." : t.contact.send}
                <Send size={16} className="ml-2" />
              </button>
            </>
          )}
        </motion.form>
      </div>

      {/* Inline form input style */}
      <style>{`
        .form-input {
          width: 100%;
          margin-top: 0.5rem;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          border: 1px solid #E5E7EB;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          font-family: 'Manrope', sans-serif;
          font-size: 0.95rem;
          color: #0A192F;
          background: #FFF;
        }
        .form-input:focus {
          border-color: #002B49;
          box-shadow: 0 0 0 3px rgba(0, 43, 73, 0.1);
        }
      `}</style>
    </section>
  );
};

const ContactInfo = ({ Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 rounded-lg bg-[#002B49] flex items-center justify-center flex-shrink-0">
      <Icon size={18} className="text-[#C5A059]" />
    </div>
    <div>
      <div className="text-xs uppercase tracking-wider text-[#64748B] font-medium">
        {label}
      </div>
      <div className="text-[#0A192F] font-medium mt-0.5">{value}</div>
    </div>
  </div>
);

const Field = ({ label, required, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
      {label} {required && <span className="text-[#C5A059]">*</span>}
    </span>
    {children}
  </label>
);

export default Contact;
